import * as THREE from "./vendor__three__three.module.js?v=272c968f89";
import { OrbitControls } from "./vendor__three__addons__controls__OrbitControls.js?v=abe4952115";
import { GLTFLoader } from "./vendor__three__addons__loaders__GLTFLoader.js?v=f0db271c5b";
import { productionSkin, validateManifest, validatePreviewManifest, validateImage, planArtwork, wrapGeometryData, topFootprintRadius, jarFrontAzimuth, ARTWORK_WARNING } from "./jars-3d__cad__jar-artwork.js?v=56543d58b2";
import { prefetchJarRelief, clearReliefPrefetch } from "./jars-3d__cad__jar-emboss-cache.js?v=d23b47ad6d";
import { createJarFinishSystem } from "./jars-3d__cad__jar-finishes.js?v=8cfcd46ecb";
import { JAR_EMBOSS_MM } from "./jars-3d__cad__jar-emboss-mask.js?v=12e3e69c88";
import { validateEmbossManifest, validateEmbossImage, loadVerifiedArtworkImage } from "./jars-3d__cad__jar-emboss-assets.js?v=51220def51";

const MANIFEST_URL = new URL("./jars-3d__production-art__manifest.json?v=cd7a2c1bc9", import.meta.url);
const PREVIEW_MANIFEST_URL = new URL("./jars-3d__preview-art__seams-v1__manifest.json?v=9630c98266", import.meta.url);
const EMBOSS_MANIFEST_URL = new URL("./jars-3d__emboss-art__manifest.json?v=e91936688a", import.meta.url);
const JARS = { eris: "5ml", ceres: "5ml", black4oz: "4oz", saturn: "150ml" };
// The 5ml jars frame inside this radius (mm) and keep the tuned emboss; bigger jars draw smaller
// on screen, so their relief is raised by their frame radius over this.
const RELIEF_REFERENCE_MM = 26;
const pending = {
  jar: "eris", labels: true, lidWrap: true, finish: { material: "gloss", laminate: "gloss", emboss: "none", white: "spot" },
  view: { polar: THREE.MathUtils.degToRad(68), azimuth: THREE.MathUtils.degToRad(17.5) },
  // A stand-in clip is covering the canvas (vp-standin.js): build and dress the jar, but don't reveal,
  // animate or draw it until the clip hands over (seekIntro) or the hold is released.
  hold: false,
  // The page shows its fixed still of the jar at rest while it loads (no clip holding it): the live jar then takes
  // over in that pose instead of popping its lid open (stage rule, 10/12 review round 3).
  fromStill: false,
};
const finishQueue = [];

const LID_SECONDS = 2.2;              // full unscrew/screw travel
const INTRO_HOLD = 0.35;              // seconds the open jar sits before the lid goes on
const RELIEF_WAIT = 1.5;              // most seconds a hidden labeled jar waits on its raised emboss
const SPIN_RATE = Math.PI * 2 / 30;   // turntable: one turn every 30 s
const SPIN_EASE_IN = 1.2;             // seconds to reach full turntable speed
const SPIN_RESUME = 4;                // seconds after a drag or view pick before turning again
const LOST_GRACE_MS = 2000;           // a lost WebGL context gets this long (page visible) to come back before the page shows its still

// Labeled jars tone-map with Neutral: ACES Filmic pulled the label art toward white, a milky haze over the print that
// grew at the jar's sides (Josh 2026-10-17: "the haze especially at certain angles is too strong"). Neutral keeps the
// art file's colours and deep blacks. Bare glass (jars.html) keeps ACES, so the empty jars look exactly as before.
const toneMappingFor = labels => labels ? THREE.NeutralToneMapping : THREE.ACESFilmicToneMapping;

// Stage backdrops (Josh 2026-10-17: the jar previews should get a gradient like the die-cut stage; tried on the 4oz,
// then C on every jar). Each one is the die-cut stage's CSS stack (builder-theme.css .product-preview::before): a soft light
// centre over a 110° tint over a base colour, on a layer 12% bigger than the stage each side. Painted once into a
// canvas and used as scene.background: an sRGB texture is not tone mapped, so the colours land as written.
// a = die-cut match, b = blue leaning, c = pearl (ChatGPT's pick). ?jarbg=a|b|c|off previews any of them on any jar.
const STAGE_BACKDROPS = {
  a: { base: "#cbcbcb",
    tint: [[0, "rgba(237,30,41,.12)"], [.16, "rgba(237,31,146,.09)"], [.33, "rgba(62,83,163,.14)"], [.5, "rgba(76,199,237,.12)"],
      [.67, "rgba(108,191,70,.1)"], [.83, "rgba(242,235,23,.13)"], [1, "rgba(239,44,37,.12)"]],
    glow: [[0, "rgba(223,223,223,.96)"], [.38, "rgba(206,206,206,.92)"], [.8, "rgba(206,206,206,0)"]] },
  b: { base: "#d0d9df",
    tint: [[0, "rgba(62,83,163,.15)"], [.5, "rgba(76,199,237,.12)"], [1, "rgba(62,83,163,.1)"]],
    glow: [[0, "rgba(235,237,238,.98)"], [.38, "rgba(221,226,229,.94)"], [.8, "rgba(221,226,229,0)"]] },
  c: { base: "#d8d5d0",
    tint: [[0, "rgba(237,31,146,.05)"], [.33, "rgba(62,83,163,.08)"], [.66, "rgba(76,199,237,.05)"], [1, "rgba(242,235,23,.07)"]],
    glow: [[0, "rgba(239,238,235,.98)"], [.38, "rgba(226,225,222,.94)"], [.8, "rgba(226,225,222,0)"]] }
};
const JAR_BACKDROP = { ceres: "c", eris: "c", black4oz: "c", saturn: "c" };   // a jar not listed keeps the white stage
const BACKDROP_PICK = (() => { try { return new URLSearchParams(location.search).get("jarbg") || ""; } catch (ignored) { return ""; } })();

function paintBackdrop(spec, size = 1024) {
  const c = document.createElement("canvas"); c.width = c.height = size;
  const g = c.getContext("2d"), box = size * 1.24, mid = size / 2;
  g.fillStyle = spec.base; g.fillRect(0, 0, size, size);
  // linear-gradient(110deg): through the layer's centre toward 110° (0° = up), as long as the layer needs.
  const angle = 110 * Math.PI / 180, dx = Math.sin(angle), dy = -Math.cos(angle), half = box * (Math.abs(dx) + Math.abs(dy)) / 2;
  const tint = g.createLinearGradient(mid - dx * half, mid - dy * half, mid + dx * half, mid + dy * half);
  spec.tint.forEach(([at, color]) => tint.addColorStop(at, color));
  g.fillStyle = tint; g.fillRect(0, 0, size, size);
  // radial-gradient(ellipse at 50% 44%), farthest-corner: radii √2 × the distances to the farthest sides.
  const cy = mid - box / 2 + box * 0.44, rx = Math.SQRT2 * box * 0.5, ry = Math.SQRT2 * box * 0.56;
  g.translate(mid, cy); g.scale(1, ry / rx);
  const glow = g.createRadialGradient(0, 0, 0, 0, 0, rx);
  spec.glow.forEach(([at, color]) => glow.addColorStop(at, color));
  g.fillStyle = glow; g.fillRect(-2 * rx, -2 * rx, 4 * rx, 4 * rx);
  const texture = new THREE.CanvasTexture(c);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

// Stand-in hand-off (vp-standin.js, 10/11): the intro as a pure function of seconds since the reveal.
// The clip renderer (tools/standins) and the live hand-off both use it, so a clip frame and the live jar
// agree exactly. The knobs above stay the only source of the timing.
const DRIFT_RATE = Math.PI * 2 / 14;  // jar-finishes.js lighting drift speed
// Integral on [0, p] of the turntable's ease-in (createPreview's `ease`: cubic in-out).
const introEaseArea = p => p < 0.5 ? p * p * p * p : p - 0.5 + Math.pow(2 - 2 * p, 4) / 16;
function introPose(t) {
  t = Math.max(0, Number(t) || 0);
  if (t < INTRO_HOLD) return { introPending: true, introHold: INTRO_HOLD - t, lidT: 1, lidTarget: 1, spinRamp: 0, spinAngle: 0, phase: t * DRIFT_RATE };
  const s = t - INTRO_HOLD;
  if (s < LID_SECONDS) return { introPending: false, introHold: 0, lidT: 1 - s / LID_SECONDS, lidTarget: 0, spinRamp: 0, spinAngle: 0, phase: t * DRIFT_RATE };
  const u = s - LID_SECONDS, r = Math.min(1, u / SPIN_EASE_IN);
  const turned = SPIN_EASE_IN * introEaseArea(r) + Math.max(0, u - SPIN_EASE_IN);
  return { introPending: false, introHold: 0, lidT: 0, lidTarget: 0, spinRamp: r, spinAngle: -(SPIN_RATE * turned) % (Math.PI * 2), phase: t * DRIFT_RATE };
}
// Reduced motion: no intro, no turn, no drift; the jar at rest.
const REST_POSE = { introPending: false, introHold: 0, lidT: 0, lidTarget: 0, spinRamp: 0, spinAngle: 0, phase: 0 };
function vpMark(name, detail) {
  try { performance.mark(name, detail === undefined ? undefined : { detail }); } catch (error) { try { performance.mark(name); } catch (ignored) {} }
}

const BARE_STRIP = 9;
// Bare-glass studio for the empty-jar shop (the label builder keeps studioEnv).
// The camera looks down, so a glass wall reflects slightly below the horizon:
// these strips run floor to ceiling so the jar shows photo-style vertical highlights.
function bareGlassEnv() {
  const sc = new THREE.Scene();
  sc.background = new THREE.Color(0x030303);
  const panel = (w, h, x, y, z, intensity) => {
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color().setScalar(intensity), side: THREE.DoubleSide }));
    mesh.position.set(x, y, z); mesh.lookAt(0, 0, 0); sc.add(mesh);
  };
  panel(6, 3, 0, 7, 2, 14);
  panel(2.2, 24, -5.5, 0, 5.5, BARE_STRIP);
  panel(1.4, 24, 6, 0, 4.5, BARE_STRIP * 0.55);
  panel(14, 6, 0, -1, 9, 0.22);
  return sc;
}
let active = null;
let lastReport = { jar: pending.jar, status: "idle", errors: [], warnings: [ARTWORK_WARNING] };

function disposeTree(root) {
  if (!root) return;
  const geometries = new Set(), materials = new Set(), textures = new Set();
  root.traverse(node => {
    if (node.geometry) geometries.add(node.geometry);
    for (const material of Array.isArray(node.material) ? node.material : [node.material]) {
      if (!material) continue;
      materials.add(material);
      for (const value of Object.values(material)) if (value?.isTexture) textures.add(value);
    }
    node.shadow?.dispose();
  });
  geometries.forEach(geometry => geometry.dispose());
  materials.forEach(material => material.dispose());
  textures.forEach(texture => texture.dispose());
}

// Studio CAD geometry stays in local millimetres; its parent nodes supply the 0.001 scale.
function nodeBox(node) {
  const geometry = node.geometry;
  if (!geometry.boundingBox) geometry.computeBoundingBox();
  return geometry.boundingBox;
}

function wallExtent(node) {
  const p = node.geometry.attributes.position;
  let R = 0, top = -Infinity;
  for (let i = 0; i < p.count; i++) { R = Math.max(R, Math.hypot(p.getX(i), p.getZ(i))); top = Math.max(top, p.getY(i)); }
  const byY = new Map();
  let flatR = 0;
  for (let i = 0; i < p.count; i++) {
    const r = Math.hypot(p.getX(i), p.getZ(i)), y = p.getY(i);
    if (y > top - 0.05) flatR = Math.max(flatR, r);
    if (r < 0.97 * R) continue;
    const k = Math.round(y * 100);
    if (!byY.has(k) || byY.get(k) < r) byY.set(k, r);
  }
  const pts = [...byY.entries()].map(([k, r]) => [k / 100, r]).sort((a, b) => a[0] - b[0]);
  let best = null, run = null;
  for (let i = 1; i < pts.length; i++) {
    const [y0, r0] = pts[i - 1], [y1, r1] = pts[i];
    const vertical = y1 - y0 > 1e-3 && Math.abs(r1 - r0) / (y1 - y0) <= 0.06;
    if (vertical) { run = run ? { y0: run.y0, y1 } : { y0, y1 }; if (!best || run.y1 - run.y0 > best.y1 - best.y0) best = { ...run }; }
    else run = null;
  }
  const y0 = best ? best.y0 : 0, y1 = best ? best.y1 : top;
  return { R, y0, y1, top, flatR, r0: byY.get(Math.round(y0 * 100)) ?? R, r1: byY.get(Math.round(y1 * 100)) ?? R };
}

function modelDimensions(model) {
  return Object.fromEntries(["id", "D", "H", "bodyR", "lidR", "lidH", "lidBottom", "bodyTop", "bodyWall", "lidWall", "meta"].map(key => [key, model[key]]));
}

function studioEnv() {
  const sc = new THREE.Scene();
  sc.background = new THREE.Color(0x050505);
  const box = (w, h, x, y, z, ry, intensity) => {
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color().setScalar(intensity), side: THREE.DoubleSide }));
    mesh.position.set(x, y, z); mesh.rotation.y = ry; mesh.lookAt(0, 0, 0); sc.add(mesh);
  };
  box(6, 3, 0, 7, 2, 0, 14);
  box(3.0, 9, -6, 2, 4, 0, 9);
  box(2.5, 9, 6, 2, 3.5, 0, 5);
  box(10, 4, 0, 1, -9, 0, 0.35);
  box(12, 6, 0, 2.5, 9, 0, 0.30);
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(30, 30), new THREE.MeshBasicMaterial({ color: 0x505050, side: THREE.DoubleSide }));
  floor.rotation.x = -Math.PI / 2; floor.position.y = -1; sc.add(floor);
  return sc;
}

// The page may have started this request with its first bytes (vp-standin.js: window.vpEarly); take that response
// once instead of requesting the file again. Safari never reuses <link rel=preload as=fetch> for fetch().
function earlyFetch(url, init) {
  const started = window.vpEarly?.take?.(String(url));
  return started ? started.then(r => (r.ok ? r : fetch(url, init)), () => fetch(url, init)) : fetch(url, init);
}

// Versioned URLs (the theme's ?v=<content hash>) can use the normal cache; unversioned local ones revalidate.
function cacheMode(url) {
  try { return new URL(String(url), location.href).searchParams.has("v") ? "default" : "no-cache"; }
  catch (error) { return "no-cache"; }
}

async function loadManifest(url, signal) {
  const response = await earlyFetch(url, { cache: cacheMode(url), signal });
  if (!response.ok) throw new Error(`Artwork manifest failed (${response.status}): ${url}`);
  return response.json();
}

async function loadCutOutline(art, signal) {
  if (!art.cutShapeUrl) return null;
  const response = await fetch(art.cutShapeUrl, { cache: cacheMode(art.cutShapeUrl), signal });
  if (!response.ok) throw new Error(`Original die contour failed (${response.status}): ${art.cutShapeUrl}`);
  const documentSvg = new DOMParser().parseFromString(await response.text(), "image/svg+xml");
  const svg = documentSvg.documentElement, paths = svg.querySelectorAll("path");
  const box = (svg.getAttribute("viewBox") || "").trim().split(/[\s,]+/).map(Number);
  if (documentSvg.querySelector("parsererror") || paths.length !== 1 || box.length !== 4 || !box.every(Number.isFinite) || box[2] <= 0 || box[3] <= 0) throw new Error("Original die contour SVG is invalid.");
  const path = paths[0], length = path.getTotalLength();
  if (!Number.isFinite(length) || length <= 0) throw new Error("Original die contour has no usable path.");
  const count = Math.min(8192, Math.max(512, Math.ceil(length * art.widthMm / box[2] / 0.1)));
  return Array.from({ length: count }, (_, i) => {
    const p = path.getPointAtLength(length * i / count);
    return [(p.x - box[0]) / box[2] * art.widthMm, (1 - (p.y - box[1]) / box[3]) * art.heightMm];
  });
}

function createPreview(element) {
  let canvas = element, alive = true;
  let renderer = null, pmrem = null, environmentTarget = null, scene = null;
  let camera = null, controls = null, finishSystem = null, key = null, fill = null;
  let observer = null, raf = 0, sized = false, loading = null;
  let jar = null, labelMeshes = [], jarGen = 0, labelGen = 0, loadAbort = null, withLabels = pending.labels, bareEnvTarget = null;
  let lidT = 0, lidOffset = 0, lidTarget = 0;
  // Page-open intro: the jar arrives with its lid off, the lid spins shut, then the jar keeps turning.
  let introPending = false, introHold = 0, spinAngle = 0, spinRamp = 0, spinIdle = 0, dragging = false, revealWait = 0;
  let held = pending.hold === true, introClock = false, lidClosedMarked = false;
  let report = { jar: pending.jar, status: "loading-model", errors: [], warnings: [ARTWORK_WARNING] };
  const backdrops = new Map(), whiteStage = new THREE.Color(0xffffff);
  const stageBackground = id => {
    const name = BACKDROP_PICK || JAR_BACKDROP[id], spec = STAGE_BACKDROPS[name];
    if (!spec) return whiteStage;
    if (!backdrops.has(name)) backdrops.set(name, paintBackdrop(spec));
    return backdrops.get(name);
  };
  const gltfLoader = new GLTFLoader(), imgLoader = new THREE.ImageLoader();
  const clock = new THREE.Clock();
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const lightView = new THREE.Vector3(), lightUp = new THREE.Vector3(), lightRight = new THREE.Vector3();
  const lastShadowLight = new THREE.Vector3(Infinity, Infinity, Infinity);
  const lastShadowTarget = new THREE.Vector3(Infinity, Infinity, Infinity);
  const ease = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const current = gen => alive && gen === jarGen;
  const currentLabels = (gen, model) => alive && gen === labelGen && jar === model;
  // iOS drops the WebGL context under memory pressure and when Safari is backgrounded; three draws nothing while it
  // is lost. Nothing counts as ready then (10/12, Sol/Astra review), so a stand-in clip keeps playing instead of
  // handing off onto a blank canvas.
  const contextLost = () => { try { return !!renderer?.getContext()?.isContextLost(); } catch (error) { return false; } };

  function refreshState() {
    if (!canvas) return;
    canvas.dataset.state = report.status === "ready" ? "ready" : report.status === "error" ? "error" : "loading";
    canvas.dataset.artworkState = report.status;
    canvas.setAttribute("aria-busy", String(report.status.startsWith("loading-")));
  }

  function fail(error) {
    if (!alive) return;
    const text = String(error?.message || error);
    report.status = "error";
    if (!report.errors.includes(text)) report.errors.push(text);
    refreshState();
    console.warn("jar-preview-3d:", text);
  }

  // The viewer died on its own (boot or a frame threw): record it, tear down, and tell the page so it can show its
  // fixed still instead of a dead canvas (jar-builder-preview.js listens; 10/12).
  function die(error) {
    const el = canvas;
    fail(error); destroy();
    try { el?.dispatchEvent(new CustomEvent("jarpreviewdead")); } catch (ignored) {}
  }

  function warnFinish(mesh, error) {
    const text = `${report.jar} ${mesh.name}: finish unavailable; base artwork retained. ${error?.message || error}`;
    if (!report.warnings.includes(text)) report.warnings.push(text);
    console.warn("jar-preview-3d:", text);
  }

  function attachFinish(mesh, piece, id) {
    try { finishSystem.attach(mesh, piece); }
    catch (error) {
      finishSystem.release(mesh);
      if (id === "saturn") throw error;
      warnFinish(mesh, error);
    }
  }

  function setFinish(patch) {
    if (!alive || !finishSystem) return;
    try { pending.finish = finishSystem.set(patch); }
    catch (error) {
      // A newly requested relief can reject one UV grid after its flat finish attached.
      labelMeshes.forEach(mesh => finishSystem.release(mesh));
      try {
        pending.finish = finishSystem.set(patch);
        for (const mesh of labelMeshes) attachFinish(mesh, mesh.userData.artwork, jar.id);
      } catch (retryError) { fail(retryError); return; }
    }
    paint();
  }

  function disposeLabel(mesh) {
    finishSystem?.release(mesh);
    mesh.removeFromParent();
    disposeTree(mesh);
  }

  function disposeJar() {
    labelMeshes.forEach(disposeLabel);
    labelMeshes = [];
    // The shared bare-glass environment outlives each jar; detach it before disposing materials.
    if (jar) { glassMaterials(jar).forEach(material => { if (material.envMap === bareEnvTarget?.texture) material.envMap = null; }); jar.root.removeFromParent(); disposeTree(jar.root); }
    jar = null;
  }

  async function buildJar(id, gen, signal) {
    const url = new URL((window.VP_CDN+"jars-3d__models__") + id + ".glb", import.meta.url);
    const response = await earlyFetch(url, { signal });
    if (!response.ok) throw new Error(`${id}.glb failed to load (${response.status})`);
    const bytes = await response.arrayBuffer();
    if (!current(gen)) return null;
    const gltf = await gltfLoader.parseAsync(bytes, new URL(".", url).href);
    if (!current(gen)) { gltf.scenes.forEach(disposeTree); return null; }
    // Uncached GLBs are owned by this mount, so their meshes need no shared clone.
    const root = gltf.scene, parts = {};
    try {
      root.traverse(node => {
        if (node.isMesh) {
          node.castShadow = true; node.receiveShadow = true;
          for (const material of Array.isArray(node.material) ? node.material : [node.material]) {
            if (material) material.envMapIntensity = 1.0;
          }
        }
        if (["body", "thread", "lid", "liner"].includes(node.name)) parts[node.name] = node;
      });
      if (!parts.body || !parts.lid) throw new Error(`${id}.glb has no body/lid nodes (found: ${Object.keys(parts).join(", ") || "none"})`);
      const meta = root.userData?.jar || gltf.scenes?.[0]?.userData?.jar || null;
      const bodyBox = nodeBox(parts.body), lidBox = nodeBox(parts.lid);
      const bodyR = Math.max(bodyBox.max.x, -bodyBox.min.x);
      const lidR = Math.max(lidBox.max.x, -lidBox.min.x);
      const lidH = meta?.mm?.lid_h ?? (lidBox.max.y - lidBox.min.y);
      const lidBottom = meta?.lid_bottom_mm ?? parts.lid.position.y * 1000;
      const bodyTop = meta?.body_top_mm ?? Math.min(bodyBox.max.y, lidBottom - 0.3);
      const H = meta?.closed_h_mm ?? meta?.mm?.H ?? (lidBottom + lidH);
      const D = meta?.mm?.D ?? bodyR * 2;
      return {
        id, root, ...parts, meta,
        lidBase: {
          lid: parts.lid.position.y, liner: parts.liner?.position.y ?? parts.lid.position.y,
          lidRotation: parts.lid.rotation.y, linerRotation: parts.liner?.rotation.y ?? 0,
        },
        bodyR, lidR, lidH, lidBottom, bodyTop, H, D,
        bodyWall: wallExtent(parts.body), lidWall: wallExtent(parts.lid),
      };
    } catch (error) { gltf.scenes.forEach(disposeTree); throw error; }
  }

  function productionMesh(piece, image, periodic, id) {
    const texture = new THREE.Texture(image);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = periodic ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy()); texture.needsUpdate = true;
    let geometry, material, lapMaterial, mesh, depthMask = null;
    try {
      if (piece.role === "top") {
        geometry = new THREE.PlaneGeometry(piece.widthMm, piece.heightMm);
        geometry.rotateX(-Math.PI / 2); geometry.translate(0, piece.topMm, 0);
      } else {
        const data = wrapGeometryData(piece);
        geometry = new THREE.BufferGeometry();
        geometry.setAttribute("position", new THREE.Float32BufferAttribute(data.positions, 3));
        geometry.setAttribute("uv", new THREE.Float32BufferAttribute(data.uvs, 2));
        geometry.setIndex(data.indices);
        if (data.normals) geometry.setAttribute("normal", new THREE.Float32BufferAttribute(data.normals, 3));
        else geometry.computeVertexNormals();
        depthMask = data.depthMask || null;
        if (depthMask) {
          geometry.addGroup(0, depthMask.startIndex, 0);
          geometry.addGroup(depthMask.startIndex, depthMask.count, 1);
        } else if (piece.cone) {
          geometry.addGroup(0, data.lapGroupIndexCount, 0);
          geometry.addGroup(data.lapGroupIndexCount, data.indices.length - data.lapGroupIndexCount, 1);
        }
      }
      material = new THREE.MeshPhysicalMaterial({
        map: texture, transparent: true, alphaTest: 0.01, roughness: 0.55, metalness: 0, specularIntensity: 0.15,
        clearcoat: 0.06, clearcoatRoughness: 0.65, envMapIntensity: 0.8,
        polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1,
      });
      lapMaterial = depthMask ? new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: true, depthTest: true })
        : piece.cone ? material.clone() : null;
      if (piece.cone && lapMaterial) { lapMaterial.polygonOffsetFactor = -4; lapMaterial.polygonOffsetUnits = -4; }
      mesh = new THREE.Mesh(geometry, lapMaterial ? [material, lapMaterial] : material);
      if (depthMask) mesh.renderOrder = piece.rearLapMask.renderOrder;
      mesh.name = piece.name; mesh.castShadow = false; mesh.receiveShadow = true;
      const uv = geometry.attributes.uv, bounds = [Infinity, Infinity, -Infinity, -Infinity];
      for (let i = 0; i < uv.count; i++) {
        bounds[0] = Math.min(bounds[0], uv.getX(i)); bounds[1] = Math.min(bounds[1], uv.getY(i));
        bounds[2] = Math.max(bounds[2], uv.getX(i)); bounds[3] = Math.max(bounds[3], uv.getY(i));
      }
      mesh.userData.artwork = { ...piece, uv: bounds,
        uvBoundary: periodic ? (piece.sourceSection ? "source-guide-in-preview-coordinates" : "complete-preview-period") : piece.sourceSection ? "original-source-guide" : "full-die" };
      attachFinish(mesh, piece, id);
      return mesh;
    } catch (error) {
      if (mesh) disposeLabel(mesh);
      else { geometry?.dispose(); material?.dispose(); lapMaterial?.dispose(); texture.dispose(); }
      throw error;
    }
  }

  // 150ml lid wrap checkbox: the lid band artwork shows only while it is ticked.
  function applyLidWrap() {
    let changed = false;
    for (const mesh of labelMeshes) {
      if (mesh.userData.artwork?.role !== "lidRing" || mesh.visible === pending.lidWrap) continue;
      mesh.visible = pending.lidWrap; changed = true;
    }
    if (changed) paint();
  }

  // The manifests and label artwork don't depend on the model, so they load alongside the GLB (setJar)
  // and join here before anything is fitted to it.
  async function fetchProductionArt(id, signal) {
    const [manifest, previewManifest, embossManifest] = await Promise.all([
      loadManifest(MANIFEST_URL, signal), loadManifest(PREVIEW_MANIFEST_URL, signal), loadManifest(EMBOSS_MANIFEST_URL, signal),
    ]);
    const assets = validateManifest(manifest, id, MANIFEST_URL);
    const previewAssets = validatePreviewManifest(previewManifest, id, PREVIEW_MANIFEST_URL, assets);
    let previewLidRing, adaptedEmboss;
    if (id === "black4oz") {
      const source = validateManifest(manifest, "saturn", MANIFEST_URL);
      const preview = validatePreviewManifest(previewManifest, "saturn", PREVIEW_MANIFEST_URL, source);
      const plates = validateEmbossManifest(embossManifest, "saturn", EMBOSS_MANIFEST_URL, { ...source, ...preview });
      previewLidRing = { source: source.lidRing, preview: preview.lidRing };
      previewAssets.lidRing = preview.lidRing;
      adaptedEmboss = plates.lidRing;
    }
    for (const art of Object.values(previewAssets)) {
      const url = new URL(art.url); url.searchParams.set("v", art.sha256); art.url = url.href;
    }
    const displayAssets = { ...assets, ...previewAssets };
    const embossAssets = validateEmbossManifest(embossManifest, id, EMBOSS_MANIFEST_URL, displayAssets);
    if (adaptedEmboss) embossAssets.lidRing = adaptedEmboss;
    const maxTextureSize = renderer.capabilities.maxTextureSize;
    // A stand-in clip is playing the opening (vp-standin.js): its first bytes go before this art (both are big; sharing
    // the phone link, the clip's lid stalled while the art arrived no sooner). Capped there; no wait without a clip.
    if (pending.hold && window.VPStandin?.introLoaded) await window.VPStandin.introLoaded();
    if (signal?.aborted) throw new Error("Artwork load cancelled.");
    const results = await Promise.allSettled([
      ...Object.entries(displayAssets).map(async ([role, art]) => {
        const image = await loadVerifiedArtworkImage(art, imgLoader, { signal });
        validateImage(art, image, maxTextureSize);
        return [role, image];
      }),
      ...Object.entries(embossAssets).map(async ([role, plate]) => ["emboss:" + role, await loadVerifiedArtworkImage(plate, imgLoader, { signal })]),
      loadCutOutline(assets.wrap, signal).then(outline => ["wrapOutlineMm", outline]),
    ]);
    const failed = results.filter(result => result.status === "rejected").map(result => String(result.reason?.message || result.reason));
    if (failed.length) throw new Error(failed.join("\n"));
    return { assets, previewAssets, displayAssets, embossAssets, previewLidRing, images: Object.fromEntries(results.map(result => result.value)) };
  }

  async function buildProductionLabels(gen, myJar, signal, artwork) {
    const staged = [];
    report.status = "loading-artwork"; refreshState();
    try {
      for (const part of [myJar.body, myJar.lid]) {
        if (part.scale.toArray().some(v => Math.abs(v - 0.001) > 1e-8) || Math.abs(part.position.x) > 1e-8 || Math.abs(part.position.z) > 1e-8 || [part.rotation.x, part.rotation.z, part === myJar.lid ? myJar.lidBase.lidRotation : part.rotation.y].some(v => Math.abs(v) > 1e-8)) {
          throw new Error("Production artwork requires the reviewed millimetre model transforms. No artwork was fitted to this changed model.");
        }
      }
      const { assets, previewAssets, displayAssets, embossAssets, previewLidRing, images } = await (artwork || fetchProductionArt(myJar.id, signal));
      if (!currentLabels(gen, myJar)) return;
      const embossPlates = Object.fromEntries(Object.entries(embossAssets).map(([role, plate]) => {
        const image = images["emboss:" + role];
        validateEmbossImage(plate, image, images[role]);
        return [role, { ...plate, image }];
      }));
      const probe = document.createElement("canvas");
      probe.width = assets.top.pixels[0]; probe.height = assets.top.pixels[1];
      const context = probe.getContext("2d", { willReadFrequently: true });
      if (!context) throw new Error("Production artwork footprint canvas is unavailable.");
      context.drawImage(images.top, 0, 0);
      const topRadiusMm = topFootprintRadius(assets.top, context.getImageData(0, 0, probe.width, probe.height).data);
      const plan = planArtwork(modelDimensions(myJar), assets, { topRadiusMm, wrapOutlineMm: images.wrapOutlineMm, previewAssets, previewLidRing });
      plan.topFootprintRadiusMm = topRadiusMm;
      report.warnings = [...plan.warnings];
      const reliefScale = Math.max(1, Math.hypot(myJar.H / 2, Math.max(myJar.bodyR, myJar.lidR)) / RELIEF_REFERENCE_MM);
      for (const piece of plan.pieces) {
        const registeredPiece = { ...piece, embossPlate: embossPlates[piece.role], displayTextureSha256: displayAssets[piece.role].sha256, reliefScale };
        staged.push({ mesh: productionMesh(registeredPiece, images[piece.role], !!previewAssets[piece.role], myJar.id), part: piece.part });
      }
      if (!currentLabels(gen, myJar)) { staged.forEach(item => disposeLabel(item.mesh)); return; }
      for (const { mesh, part } of staged) { myJar[part].add(mesh); labelMeshes.push(mesh); }
      applyLidWrap();
      report.status = "ready";
    } catch (error) {
      staged.forEach(item => disposeLabel(item.mesh));
      if (currentLabels(gen, myJar)) fail(error);
    } finally {
      if (currentLabels(gen, myJar)) refreshState();
    }
  }

  function applyLid() {
    if (!jar) return;
    const turns = jar.meta?.mm?.thread_turns ?? 1.5;
    const pitch = jar.meta?.mm?.pitch ?? 4.2;
    const turnProgress = ease(Math.min(1, lidT / 0.72));
    const freeProgress = ease(Math.max(0, (lidT - 0.72) / 0.28));
    const totalLift = jar.lidH * 1.5;
    const threadLift = Math.min(totalLift, turns * pitch);
    const lift = threadLift * turnProgress + (totalLift - threadLift) * freeProgress;
    const rotation = turns * Math.PI * 2 * turnProgress;
    lidOffset = lift / totalLift;
    jar.lid.position.y = jar.lidBase.lid + lift / 1000;
    jar.lid.rotation.y = jar.lidBase.lidRotation + rotation;
    if (jar.liner) {
      jar.liner.position.y = jar.lidBase.liner + lift / 1000;
      jar.liner.rotation.y = jar.lidBase.linerRotation + rotation;
    }
  }

  // Camera target and distance that fit the jar with its lid raised `open` (0 = on, 1 = fully off).
  function fit(open) {
    const height = (jar.H + open * jar.lidH * 1.5) / 1000, radius = Math.max(jar.bodyR, jar.lidR) / 1000;
    const halfV = THREE.MathUtils.degToRad(camera.fov / 2);
    const halfH = Math.atan(Math.tan(halfV) * camera.aspect);
    const sphereRadius = Math.hypot(height / 2, radius);
    return { target: new THREE.Vector3(0, height / 2, 0), dist: sphereRadius / Math.sin(Math.min(halfV, halfH)) * 1.10 };
  }

  function frameCamera(keepView = false) {
    if (!jar) return;
    const { target, dist } = fit(Math.max(lidOffset, lidTarget));
    const el = Math.PI / 2 - pending.view.polar, az = pending.view.azimuth;
    const view = new THREE.Vector3(Math.cos(el) * Math.sin(az), Math.sin(el), Math.cos(el) * Math.cos(az));
    const previous = camera.position.clone().sub(controls.target);
    if (keepView && previous.lengthSq() > 1e-10) view.copy(previous).normalize();
    controls.target.copy(target);
    camera.position.copy(target).addScaledVector(view, dist);
    controls.minDistance = dist * 0.35;
    controls.maxDistance = dist * 4;
    camera.near = dist * 0.02; camera.far = dist * 20; camera.updateProjectionMatrix();
    controls.update();
  }

  function updateProductLighting() {
    lightView.copy(camera.position).sub(controls.target).normalize();
    lightUp.set(0, 1, 0).applyQuaternion(camera.quaternion);
    lightRight.set(1, 0, 0).applyQuaternion(camera.quaternion);
    key.target.position.copy(controls.target);
    key.position.copy(controls.target).addScaledVector(lightView, 0.55).addScaledVector(lightUp, 0.10).addScaledVector(lightRight, -0.06);
    fill.target.position.copy(controls.target);
    fill.position.copy(controls.target).addScaledVector(lightView, 0.50).addScaledVector(lightRight, 0.28);
    scene.environmentRotation.y = Math.atan2(lightView.x, lightView.z);
    if (jar) {
      const height = (jar.H + lidOffset * jar.lidH * 1.5) / 1000;
      const radius = (Math.max(jar.bodyR, jar.lidR) + JAR_EMBOSS_MM.heavy.line) / 1000;
      const extent = Math.hypot(height / 2, radius) + Math.abs(height / 2 - controls.target.y) + 0.001;
      const distance = key.position.distanceTo(controls.target), shadow = key.shadow, sc = shadow.camera;
      const near = Math.max(0.001, distance - extent), far = distance + extent;
      const changed = sc.right !== extent || sc.near !== near || sc.far !== far;
      if (changed) {
        sc.left = sc.bottom = -extent; sc.right = sc.top = extent; sc.near = near; sc.far = far;
        sc.updateProjectionMatrix();
      }
      shadow.bias = -0.000003 / (far - near); shadow.normalBias = 0.000002;
      if (changed || !lastShadowLight.equals(key.position) || !lastShadowTarget.equals(controls.target)) renderer.shadowMap.needsUpdate = true;
      lastShadowLight.copy(key.position); lastShadowTarget.copy(controls.target);
    }
  }

  function resize() {
    if (!alive || !renderer || !camera || !canvas) return;
    const { width, height } = canvas.getBoundingClientRect();
    if (!(width > 0 && height > 0)) { sized = false; return; }
    const w = Math.max(1, Math.round(width)), h = Math.max(1, Math.round(height));
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (sized && canvas.width === Math.floor(w * dpr) && canvas.height === Math.floor(h * dpr) && camera.aspect === w / h) return;
    renderer.setPixelRatio(dpr);
    renderer.setSize(w, h, false);
    camera.aspect = w / h; camera.updateProjectionMatrix();
    sized = true;
    if (jar) frameCamera(true);
    paint();
  }

  // Camera distance over the closed jar's resting frame: below 1 when the viewer has zoomed in.
  function viewZoom() {
    if (!jar || !camera || !controls) return 1;
    return camera.position.distanceTo(controls.target) / fit(0).dist;
  }

  function paint() {
    if (!alive || !sized || !renderer || !scene || !camera) return;
    controls?.update();
    renderer.shadowMap.needsUpdate = true;
    updateProductLighting();
    finishSystem?.update({ camera, target: controls.target, dt: 0, reducedMotion: true, zoom: viewZoom() });
    renderer.render(scene, camera);
    markDrawn();
  }

  async function setJar(id) {
    const gen = ++jarGen, labels = ++labelGen;
    loadAbort?.abort();
    const controller = new AbortController(); loadAbort = controller;
    disposeJar();
    drawn = false; stateChanged();   // nothing of the new jar is on the canvas yet (stage rule)
    const skin = productionSkin(id, JARS[id]);
    withLabels = pending.labels;
    if (renderer) renderer.toneMapping = toneMappingFor(withLabels);
    if (scene) scene.background = stageBackground(id);
    report = { jar: skin.jarId, status: "loading-model", errors: [], warnings: withLabels ? [ARTWORK_WARNING] : [] };
    refreshState();
    // Label art loads in parallel with the model; it is awaited (and its errors reported) in buildProductionLabels.
    const artwork = withLabels ? fetchProductionArt(id, controller.signal) : null;
    artwork?.catch(() => {});
    try {
      const built = await buildJar(id, gen, controller.signal);
      if (!current(gen)) { if (built) disposeTree(built.root); return; }
      if (!built) return;
      jar = built; scene.add(jar.root);
      // A labeled jar stays hidden until its labels are on, so it never shows as bare black glass first.
      jar.root.visible = !withLabels; revealWait = 0;
      if (!withLabels) dressBareGlass(jar);
      // The lid waits off the jar until the labels are on, then spins shut (skipped for reduced motion). When the page
      // shows its fixed still of this jar at rest while it loads (no clip holding it), the live jar takes over in that
      // pose instead of popping the lid open (10/12 review, round 3); a clip hands over through seekIntro instead.
      introPending = !reducedMotion.matches && !(pending.fromStill && !pending.hold); introHold = INTRO_HOLD;
      lidT = lidOffset = lidTarget = introPending ? 1 : 0;
      spinAngle = spinRamp = spinIdle = 0; introClock = lidClosedMarked = false;
      applyLid(); resize(); frameCamera();
      paint();
      // Empty-jar pages show the bare glass; only the label builder dresses it.
      if (withLabels) await buildProductionLabels(labels, jar, controller.signal, artwork);
      else { report.status = "ready"; refreshState(); }
      if (current(gen)) paint();
    } catch (error) { if (current(gen)) fail(error); }
  }

  // Bare glass is near-black; without a label it reflects its own studio (bareGlassEnv).
  function glassMaterials(model) {
    const found = [];
    for (const part of [model.body, model.thread]) {
      part?.traverse(node => {
        if (!node.isMesh) return;
        for (const material of Array.isArray(node.material) ? node.material : [node.material]) if (material) found.push(material);
      });
    }
    return found;
  }

  function dressBareGlass(model) {
    if (!bareEnvTarget) {
      const environmentScene = bareGlassEnv();
      try { bareEnvTarget = pmrem.fromScene(environmentScene, 0.02); }
      finally { disposeTree(environmentScene); }
    }
    for (const material of glassMaterials(model)) {
      material.envMap = bareEnvTarget.texture; material.envMapIntensity = 1; material.needsUpdate = true;
    }
  }

  function selectJar(id) {
    if (!alive || !finishSystem) return;
    if (report.jar === id && withLabels === pending.labels && report.status !== "error" && report.status !== "idle") return loading;
    loading = setJar(id);
    return loading;
  }

  function moveCamera(polar, azimuth) {
    if (!alive || !camera || !controls || !jar) return false;
    controls.autoRotate = false;
    const dist = camera.position.distanceTo(controls.target);
    const spherical = new THREE.Spherical(dist, THREE.MathUtils.clamp(polar, controls.minPolarAngle, controls.maxPolarAngle), azimuth);
    const damping = controls.enableDamping;
    controls.enableDamping = false; controls.update();
    camera.position.copy(controls.target).add(new THREE.Vector3().setFromSpherical(spherical));
    controls.update(); controls.enableDamping = damping;
    return true;
  }

  function look(polar, azimuth) {
    if (!moveCamera(polar, azimuth)) return;
    // A picked view shows the jar the way its thumbnail does; the turntable picks up again after a pause.
    spinAngle = spinRamp = 0; spinIdle = SPIN_RESUME; jar.root.rotation.y = 0;
    paint();
  }

  function pose() {
    if (!alive || !jar || !camera || !controls) return null;
    return { jar: jar.id, polar: controls.getPolarAngle(), azimuth: controls.getAzimuthalAngle(),
      distanceRatio: camera.position.distanceTo(controls.target) / (controls.minDistance / 0.35) };
  }

  function restorePose(saved) {
    if (!saved || saved.jar !== jar?.id || ![saved.polar, saved.azimuth, saved.distanceRatio].every(Number.isFinite)) return false;
    const distance = THREE.MathUtils.clamp(controls.minDistance / 0.35 * saved.distanceRatio, controls.minDistance, controls.maxDistance);
    pending.view = { polar: THREE.MathUtils.clamp(saved.polar, controls.minPolarAngle, controls.maxPolarAngle), azimuth: saved.azimuth };
    const damping = controls.enableDamping;
    controls.autoRotate = false; controls.enableDamping = false; controls.update();
    camera.position.copy(controls.target).add(new THREE.Vector3().setFromSpherical(new THREE.Spherical(distance, pending.view.polar, pending.view.azimuth)));
    controls.update(); controls.enableDamping = damping; paint(); return true;
  }

  // Grab one view without leaving the live camera there. The buffer is restored
  // before this returns, so the page does not flash the other angle.
  function snapshot(polar, azimuth) {
    if (!alive || !sized || !renderer || !scene || !camera || !controls || !jar || !canvas) return "";
    const savedPos = camera.position.clone(), savedTarget = controls.target.clone();
    const savedAuto = controls.autoRotate;
    const savedDamp = controls.enableDamping;
    const savedLid = lidT, savedSpin = jar.root.rotation.y, savedVisible = jar.root.visible;
    // Thumbnails show the jar at rest even mid-intro: shown, lid on, unturned, framed closed.
    jar.root.rotation.y = 0; jar.root.visible = true;
    if (lidT !== 0) {
      const rest = fit(0);
      lidT = 0; applyLid();
      controls.target.copy(rest.target);
      camera.position.copy(rest.target).addScaledVector(savedPos.clone().sub(savedTarget).normalize(), rest.dist);
    }
    let url = "";
    if (moveCamera(polar, azimuth)) {
      updateProductLighting();
      renderer.shadowMap.needsUpdate = true;
      finishSystem?.update({ camera, target: controls.target, dt: 0, reducedMotion: true, zoom: viewZoom() });
      renderer.render(scene, camera);
      try { url = canvas.toDataURL("image/jpeg", 0.72); }
      catch (error) { url = ""; }
    }
    lidT = savedLid; applyLid();
    jar.root.rotation.y = savedSpin; jar.root.visible = savedVisible;
    controls.target.copy(savedTarget);
    camera.position.copy(savedPos);
    controls.enableDamping = false; controls.update();
    controls.enableDamping = savedDamp;
    controls.autoRotate = savedAuto;
    paint();
    return url;
  }

  // Stand-in hand-off: put the jar exactly where the intro is t seconds after it showed (lid, turn,
  // lighting drift), show it and draw it now; the loop carries on from there. Reduced motion: at rest.
  // resume: the clip had stopped on this frame (it ended or stalled), so the turn eases back in as after a drag.
  function seekIntro(t, resume) {
    if (!alive || !jar || !sized || report.status !== "ready" || contextLost()) return false;
    const p = reducedMotion.matches ? REST_POSE : introPose(t);
    introPending = p.introPending; introHold = p.introHold; lidT = p.lidT; lidTarget = p.lidTarget;
    spinRamp = resume ? 0 : p.spinRamp; spinAngle = p.spinAngle; spinIdle = 0;
    introClock = true; lidClosedMarked = lidT === 0;
    jar.root.rotation.y = spinAngle; jar.root.visible = true;
    finishSystem.seekPhase?.(p.phase);
    applyLid(); frameCamera(true);
    renderer.shadowMap.needsUpdate = true;
    clock.getDelta();  // the next loop() step starts from here, not from the last frame
    held = pending.hold = false;
    vpMark("vp:jar-live-visible", { t: Math.max(0, Number(t) || 0), handoff: true });
    paint();
    return true;
  }

  // Release a hold without a hand-off: the jar then shows and runs its intro from the start.
  function setHold(on) {
    held = pending.hold = on === true;
    clock.getDelta();
  }

  // Turntable. Turning the jar (not the camera) keeps the camera-locked lights still, and the
  // lathe-turned glass and lid cast the same shadow at any angle, so the shadow map stays put.
  function turnJar(dt) {
    if (spinIdle > 0 && !dragging) spinIdle = Math.max(0, spinIdle - dt);
    const go = !introPending && lidT === 0 && lidTarget === 0 && !dragging && spinIdle === 0 && !reducedMotion.matches;
    spinRamp = go ? Math.min(1, spinRamp + dt / SPIN_EASE_IN) : 0;
    if (!spinRamp) return;
    // Same way the lid screwed on: clockwise seen from above.
    spinAngle = (spinAngle - SPIN_RATE * ease(spinRamp) * dt) % (Math.PI * 2);
    jar.root.rotation.y = spinAngle;
  }

  function loop() {
    if (!alive) return;
    try {
      const dt = Math.min(clock.getDelta(), 0.1);
      // A stand-in clip covers the canvas: the jar stays dressed but still and undrawn until it hands over. The raised
      // emboss's wait (RELIEF_WAIT) runs as it would without a clip, so revealReady() turns true when the jar would show.
      if (held) {
        if (jar && !jar.root.visible && !report.status.startsWith("loading-")) revealWait += dt;
        raf = requestAnimationFrame(loop); return;
      }
      // Reveal once the labels are on (or failed); give a raised emboss a moment to land too.
      if (jar && !jar.root.visible && !report.status.startsWith("loading-")) {
        revealWait += dt;
        if (!finishSystem.busy?.() || revealWait >= RELIEF_WAIT) { jar.root.visible = true; renderer.shadowMap.needsUpdate = true; }
      }
      // The intro's clock starts on the first frame the jar shows: the lighting drift from phase 0 and the
      // full 0.35 s hold after it (the timeline stand-in clips are rendered on).
      let first = false;
      if (!introClock && jar?.root.visible && !report.status.startsWith("loading-")) {
        introClock = first = true;
        finishSystem.seekPhase?.(0);
        vpMark("vp:jar-live-visible");
      }
      if (introPending && !first && jar?.root.visible && !report.status.startsWith("loading-")) {
        introHold -= dt;
        if (introHold <= 0) { introPending = false; lidTarget = 0; vpMark("vp:jar-lid-start"); }
      }
      if (lidT !== lidTarget) {
        lidT = lidTarget > lidT ? Math.min(1, lidT + dt / LID_SECONDS) : Math.max(0, lidT - dt / LID_SECONDS);
        applyLid();
        renderer.shadowMap.needsUpdate = true;
        // The frame settles in with the lid instead of jumping once it lands.
        frameCamera(true);
        if (lidT === 0 && lidTarget === 0 && !lidClosedMarked) { lidClosedMarked = true; vpMark("vp:jar-lid-closed"); }
      }
      if (jar) turnJar(dt);
      controls.update();
      updateProductLighting();
      finishSystem.update({ camera, target: controls.target, dt: first ? 0 : dt, reducedMotion: reducedMotion.matches || document.hidden, zoom: viewZoom() });
      if (sized) { renderer.render(scene, camera); markDrawn(); }
      raf = requestAnimationFrame(loop);
    } catch (error) { raf = 0; die(error); }
  }

  // iOS drops the WebGL context when Safari is backgrounded or short on memory, then restores it. three.js re-uploads
  // ordinary textures itself, but the PMREM lights and the one-shot shadow map lived only on the GPU, so the jar came
  // back unlit (the sticker builder showed the same on Josh's iPhone, 2026-10-12). Rebuild them on restore, the label
  // finishes' own studio and No-white film too (jar-finishes.js restoreGpu).
  function rebuildGpuLight() {
    pmrem?.dispose();
    pmrem = new THREE.PMREMGenerator(renderer);
    const oldEnv = environmentTarget, studio = studioEnv();
    try { environmentTarget = pmrem.fromScene(studio, 0.02); }
    finally { disposeTree(studio); }
    scene.environment = environmentTarget.texture;
    oldEnv?.dispose();
    if (bareEnvTarget) {
      const oldBare = bareEnvTarget, bare = bareGlassEnv();
      try { bareEnvTarget = pmrem.fromScene(bare, 0.02); }
      finally { disposeTree(bare); }
      if (jar) glassMaterials(jar).forEach(material => {
        if (material.envMap === oldBare.texture) { material.envMap = bareEnvTarget.texture; material.needsUpdate = true; }
      });
      oldBare.dispose();
    }
    // The finish system keeps its own exceptions and answers false: that is a failed restore too (10/12 review).
    if (finishSystem?.restoreGpu?.(pmrem) === false) throw new Error("the label finish light was not rebuilt after the context restore");
  }
  // 10/12 (Sol/Astra review): a throw inside PMREMGenerator.fromScene leaves three's renderer aimed at the PMREM's
  // off-screen target with tone mapping and autoClear off, so every later frame would draw off-screen (a blank or
  // frozen jar). Rebuild under a guard, put the renderer back, try once more a moment later; a second failure keeps
  // the jar drawing with whatever light it has.
  // One state for the page's stage rule (jar-builder-preview.js syncStage) instead of one-shot events (10/12 review,
  // round 3): lost = the WebGL context is lost right now; graceOver = lost for LOST_GRACE_MS with the page visible;
  // drawn = the current jar has drawn a real frame (visible, not held, context healthy) since the last boot, loss,
  // restore or jar switch. 'jarpreviewstate' fires on every change; the page re-evaluates its rule each time.
  let lostTimer = 0, restoreRetry = 0, drawn = false, graceOver = false;
  function stateChanged() { try { canvas?.dispatchEvent(new CustomEvent("jarpreviewstate")); } catch (ignored) {} }
  function canvasState() { return { alive, lost: contextLost(), graceOver, drawn, jar: pending.jar }; }
  function markDrawn() {
    if (drawn || !alive || !sized || held || !jar || jar.id !== pending.jar || !jar.root.visible || contextLost()) return;
    drawn = true; stateChanged();
  }
  function onContextRestored() {
    if (!alive || !renderer || !scene || contextLost()) return;
    try {
      rebuildGpuLight();
      restoreRetry = 0;
    } catch (error) {
      // PMREM capture may have left its off-screen target bound with tone mapping and autoClear off: put the renderer
      // back on the canvas first. One retry a moment later; a second failure ends the viewer the way a dead frame
      // does, so the page shows its fixed still instead of an unlit, blank or off-screen jar (10/12 review).
      try { renderer.setRenderTarget(null); renderer.toneMapping = toneMappingFor(withLabels); renderer.autoClear = true; } catch (ignored) {}
      console.warn("jar-preview-3d: the light rebuild after the context restore failed:", error?.message || error);
      if (restoreRetry++ < 1) { setTimeout(() => { if (alive) onContextRestored(); }, 300); return; }
      restoreRetry = 0;
      die(error);
      return;
    }
    clearTimeout(lostTimer); lostTimer = 0;
    // Restored: nothing real is on the canvas until the next frame draws the jar (loop -> markDrawn).
    graceOver = false; drawn = false; stateChanged();
    renderer.shadowMap.needsUpdate = true;
  }

  // Lost: the canvas keeps its place for LOST_GRACE_MS (counted while the page is visible) so a quick restore does not
  // blink; past that the page shows its still until the first real frame after a restore.
  function onContextLost() {
    if (!alive) return;
    clearTimeout(lostTimer);
    graceOver = false; stateChanged();
    const check = () => {
      lostTimer = 0;
      if (!alive || !contextLost() || graceOver) return;
      if (document.hidden) { lostTimer = setTimeout(check, LOST_GRACE_MS); return; }   // the grace counts while visible
      graceOver = true; stateChanged();
    };
    lostTimer = setTimeout(check, LOST_GRACE_MS);
  }

  async function boot() {
    try {
      refreshState();
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.toneMapping = toneMappingFor(pending.labels);
      renderer.toneMappingExposure = 1.0;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.shadowMap.autoUpdate = false;
      renderer.debug.onShaderError = (gl, program, vertex, fragment) => {
        throw new Error("The material preview could not render. " + [gl.getProgramInfoLog(program), gl.getShaderInfoLog(vertex), gl.getShaderInfoLog(fragment)].filter(Boolean).join("\n"));
      };
      pmrem = new THREE.PMREMGenerator(renderer);
      const environmentScene = studioEnv();
      try { environmentTarget = pmrem.fromScene(environmentScene, 0.02); }
      finally { disposeTree(environmentScene); }
      scene = new THREE.Scene();
      scene.background = stageBackground(pending.jar);
      scene.environment = environmentTarget.texture; scene.environmentIntensity = 0.45;
      canvas.addEventListener("webglcontextrestored", onContextRestored);
      canvas.addEventListener("webglcontextlost", onContextLost);
      key = new THREE.DirectionalLight(0xffffff, 2.4);
      key.castShadow = true; key.shadow.mapSize.set(4096, 4096);
      key.shadow.camera.left = key.shadow.camera.bottom = -0.12;
      key.shadow.camera.right = key.shadow.camera.top = 0.12;
      key.shadow.camera.near = 0.05; key.shadow.camera.far = 2;
      key.shadow.radius = 1;
      scene.add(key, key.target);
      fill = new THREE.DirectionalLight(0xffffff, 0.8); scene.add(fill, fill.target);
      scene.add(new THREE.HemisphereLight(0xffffff, 0xdddddd, 0.65));
      const ground = new THREE.Mesh(new THREE.CircleGeometry(1, 64), new THREE.ShadowMaterial({ opacity: 0.14 }));
      ground.rotation.x = -Math.PI / 2; ground.receiveShadow = true; scene.add(ground);
      camera = new THREE.PerspectiveCamera(16, 1, 0.005, 20);
      controls = new OrbitControls(camera, canvas);
      controls.enableDamping = true; controls.dampingFactor = 0.08;
      controls.minPolarAngle = 0.06; controls.maxPolarAngle = Math.PI / 2 - 0.035;
      controls.enablePan = false; controls.autoRotate = false;
      // A drag stops the turntable; it eases back in SPIN_RESUME seconds after the last touch.
      controls.addEventListener("start", () => { dragging = true; spinRamp = 0; });
      controls.addEventListener("end", () => { dragging = false; spinIdle = SPIN_RESUME; });
      finishSystem = createJarFinishSystem({ renderer, pmrem,
        // Raised relief lands after set() returns; tell the page so it can refresh its thumbnails.
        onChange: () => { if (alive && canvas) canvas.dispatchEvent(new CustomEvent("jarfinishchange")); } });
      pending.finish = finishSystem.set(pending.finish);
      while (finishQueue.length) setFinish(finishQueue.shift());
      if (typeof ResizeObserver !== "undefined") {
        observer = new ResizeObserver(resize); observer.observe(canvas);
      }
      window.addEventListener("resize", resize);
      window.visualViewport?.addEventListener("resize", resize);
      resize();
      loading = setJar(pending.jar);
      loop();
      await loading;
    } catch (error) { die(error); }
  }

  function destroy() {
    if (!alive) return;
    alive = false; ++jarGen; ++labelGen;
    loadAbort?.abort(); loadAbort = null;
    if (raf) cancelAnimationFrame(raf);
    raf = 0; clock.stop();
    observer?.disconnect(); observer = null;
    window.removeEventListener("resize", resize);
    window.visualViewport?.removeEventListener("resize", resize);
    controls?.dispose(); controls = null;
    disposeJar();
    finishSystem?.dispose(); finishSystem = null;
    disposeTree(scene); scene = null;
    backdrops.forEach(texture => texture.dispose()); backdrops.clear();
    environmentTarget?.dispose(); environmentTarget = null;
    bareEnvTarget?.dispose(); bareEnvTarget = null;
    pmrem?.dispose(); pmrem = null;
    canvas?.removeEventListener("webglcontextrestored", onContextRestored);
    canvas?.removeEventListener("webglcontextlost", onContextLost);
    clearTimeout(lostTimer); lostTimer = 0;
    // Recovery reuses this canvas, so keep its context available after releasing GPU resources.
    if (renderer) { renderer.dispose(); renderer = null; }
    if (canvas) {
      canvas.setAttribute("aria-busy", "false");
      if (report.status !== "error") { canvas.dataset.state = "disposed"; canvas.dataset.artworkState = "disposed"; }
    }
    camera = key = fill = null;
    canvas = element = null; sized = false; loading = null;
  }

  function diagnostics() {
    const motion = jar ? { closed: lidT === 0 && lidTarget === 0, lidProgress: lidT,
      rootYaw: jar.root.rotation.y, lidYaw: jar.lid.rotation.y, bodyYaw: jar.body.rotation.y } : null;
    const band = labelMeshes.find(mesh => mesh.userData.artwork?.previewFrontAlignment)?.userData.artwork;
    const body = labelMeshes.find(mesh => mesh.userData.artwork?.role === "wrap" && mesh.userData.artwork?.part === "body")?.userData.artwork;
    let alignment = null;
    if (motion && band && body) {
      const sunWorldYaw = band.seamAngleRadians + Math.PI * 2 * band.previewFrontAlignment.sourceU + motion.lidYaw + motion.rootYaw;
      const bodyWorldYaw = body.seamAngleRadians + Math.PI * 2 * band.previewFrontAlignment.bodySourceU + motion.bodyYaw + motion.rootYaw;
      const delta = sunWorldYaw - bodyWorldYaw;
      alignment = { sunWorldYaw, bodyWorldYaw, closedErrorRadians: Math.atan2(Math.sin(delta), Math.cos(delta)) };
    }
    return { jar: report.jar, status: report.status, errors: [...report.errors], warnings: [...report.warnings], motion, alignment };
  }

  return {
    boot, destroy, resize, selectJar, setFinish, applyLidWrap, look, snapshot, pose, restorePose, seekIntro, setHold,
    introState: () => ({ held, introClock, introPending, introHold, lidT, lidTarget, spinRamp, spinAngle, visible: !!jar?.root.visible, phase: finishSystem?.phase?.() ?? null }),
    canvasState,
    get held() { return held; },
    get canvas() { return canvas; },
    get alive() { return alive; },
    get loading() { return loading; },
    get canSetFinish() { return alive && !!finishSystem; },
    // Raised relief builds off-thread; snapshots wait for it so thumbnails match the live view.
    ready: () => alive && !!jar && jar.id === pending.jar && report.status === "ready" && !finishSystem?.busy?.() && !contextLost(),
    // The moment the jar would show without a clip: labels on, and a raised emboss done or given RELIEF_WAIT.
    revealReady: () => alive && !!jar && jar.id === pending.jar && report.status === "ready" && (!finishSystem?.busy?.() || revealWait >= RELIEF_WAIT) && !contextLost(),
    diagnostics,
    finishDiagnostics: () => finishSystem?.diagnostics() || null,
  };
}

function mount(canvas) {
  if (!canvas) return Promise.resolve();
  if (active?.canvas === canvas && active.alive) { active.resize(); return active.loading || Promise.resolve(); }
  dispose();
  active = createPreview(canvas);
  return active.boot();
}

function set(opts) {
  if (!opts || typeof opts !== "object") return;
  const previousJar = pending.jar, previousLabels = pending.labels;
  if (opts.jar != null) {
    if (Object.prototype.hasOwnProperty.call(JARS, opts.jar)) pending.jar = opts.jar;
    else console.warn("jar-preview-3d: unknown jar:", opts.jar);
  }
  if (opts.labels !== undefined) pending.labels = opts.labels !== false;
  if (previousJar !== pending.jar || previousLabels !== pending.labels) pending.view.azimuth = jarFrontAzimuth(pending.jar, pending.labels);
  if (opts.lidWrap !== undefined) { pending.lidWrap = opts.lidWrap !== false; active?.applyLidWrap(); }
  if (opts.hold !== undefined && (opts.hold === true) !== pending.hold) { pending.hold = opts.hold === true; active?.setHold(pending.hold); }
  if (opts.fromStill !== undefined) pending.fromStill = opts.fromStill === true;
  const patch = Object.fromEntries(["material", "laminate", "emboss", "white"].filter(key => opts[key] !== undefined).map(key => [key, opts[key]]));
  if (Object.keys(patch).length) {
    if (active?.canSetFinish) active.setFinish(patch);
    else finishQueue.push(patch);
  }
  const requestedEmboss = opts.emboss ?? pending.finish.emboss;
  if (!pending.labels || requestedEmboss === "none") clearReliefPrefetch();
  if (pending.labels && (requestedEmboss === "heavy" || requestedEmboss === "std")) prefetchJarRelief(pending.jar);
  active?.selectJar(pending.jar);
}

function dispose() {
  if (active) clearReliefPrefetch();
  active?.destroy(); active = null;
  lastReport = { jar: pending.jar, status: "disposed", errors: [], warnings: [ARTWORK_WARNING] };
}

function look(polar, azimuth) {
  if (!Number.isFinite(polar) || !Number.isFinite(azimuth)) return;
  pending.view = { polar: THREE.MathUtils.clamp(polar, 0.06, Math.PI / 2 - 0.035), azimuth };
  active?.look(pending.view.polar, pending.view.azimuth);
}

function snapshot(polar, azimuth) {
  if (!active?.ready() || !Number.isFinite(polar) || !Number.isFinite(azimuth)) return "";
  return active.snapshot(polar, azimuth);
}

window.jarArtworkDiagnostics = () => active ? active.diagnostics() : { ...lastReport, jar: pending.jar, errors: [...lastReport.errors], warnings: [...lastReport.warnings] };
window.jarFinishDiagnostics = () => active?.finishDiagnostics() || null;
window.VPJarPreview = { mount, set, dispose, ready: () => active?.ready() === true, revealReady: () => active?.revealReady() === true, look, snapshot,
  // Stand-in hand-off (vp-standin.js): draw the live jar at intro time t and let it run; or release the hold.
  seekIntro: (t, resume) => active?.seekIntro(t, resume === true) === true, release: () => { pending.hold = false; active?.setHold(false); },
  held: () => !!active?.held, introState: () => active?.introState() || null, canvasState: () => active?.canvasState() || null, introPose,
  frontAzimuth: (jarId = pending.jar, labels = pending.labels) => jarFrontAzimuth(jarId, labels),
  pose: () => active?.pose() || null, restorePose: saved => active?.restorePose(saved) === true };
// The module loads async: tell the page adapter the moment the viewer exists.
try { window.dispatchEvent(new Event("vpjarpreview")); } catch (error) {}
